import { isAfter, subDays, subMonths } from "date-fns";

import { PublicDailySessionDurationAggregate } from "@classview/shared-types";

import { ChartDataPoint, GroupedChartDataPoint } from "../types/chart-data";
import { Timeframe } from "../types/timeframe";

/**
 * Groups daily chart data by the selected timeframe for chart display.
 * - 6m: by month (Jan, Feb, ...)
 * - m: by week (1. week, 2. week, ...)
 * - w: by day of week (Monday, ...)
 * @param data Array of daily chart data
 * @param timeframe Selected timeframe
 * @returns Array of grouped chart data with 'name' and 'count'
 */
export function groupChartData(
  data: ChartDataPoint[],
  timeframe: Timeframe,
): GroupedChartDataPoint[] {
  // Filter data by timeframe
  const now = new Date();
  let filteredData: ChartDataPoint[] = [];
  if (timeframe === "6m") {
    const sixMonthsAgo = subMonths(now, 6);
    filteredData = data.filter((d) => isAfter(new Date(d.day), sixMonthsAgo));
  } else if (timeframe === "m") {
    const oneMonthAgo = subMonths(now, 1);
    filteredData = data.filter((d) => isAfter(new Date(d.day), oneMonthAgo));
  } else if (timeframe === "w") {
    const sevenDaysAgo = subDays(now, 7);
    filteredData = data.filter((d) => isAfter(new Date(d.day), sevenDaysAgo));
  } else {
    filteredData = data;
  }

  // Always group by day
  const sortedData = filteredData
    .sort((a, b) => a.day - b.day)
    .map((point) => ({
      ...point,
      movingAvg: null,
      trend: null,
    }));

  return sortedData;
}

export const durationRanges = [
  { min: 0, max: 5, label: "<5" },
  { min: 5, max: 10, label: "5-10" },
  { min: 10, max: 30, label: "10-30" },
  { min: 30, max: 45, label: "30-45" },
  { min: 45, max: 60, label: "45-60" },
  { min: 60, max: 90, label: "60-90" },
  { min: 90, max: 120, label: "90-120" },
  { min: 120, max: 150, label: "120-150" },
  { min: 150, max: Infinity, label: "150+" },
];

export function groupByDurationRange(data: GroupedChartDataPoint[]) {
  return durationRanges.map((range) => {
    const sessionsInRange = data.filter(
      (session) => session.count >= range.min && session.count < range.max,
    );
    return {
      range: range.label,
      count: sessionsInRange.length,
    };
  });
}

export function groupAverageSessionDuration(
  data: PublicDailySessionDurationAggregate[],
  timeframe: Timeframe,
): GroupedChartDataPoint[] {
  // Filter data by timeframe
  const now = new Date();
  let filteredData: PublicDailySessionDurationAggregate[] = [];
  if (timeframe === "6m") {
    const sixMonthsAgo = subMonths(now, 6);
    filteredData = data.filter((d) =>
      isAfter(new Date(d.dayBucket), sixMonthsAgo),
    );
  } else if (timeframe === "m") {
    const oneMonthAgo = subMonths(now, 1);
    filteredData = data.filter((d) =>
      isAfter(new Date(d.dayBucket), oneMonthAgo),
    );
  } else if (timeframe === "w") {
    const sevenDaysAgo = subDays(now, 7);
    filteredData = data.filter((d) =>
      isAfter(new Date(d.dayBucket), sevenDaysAgo),
    );
  } else {
    filteredData = data;
  }

  // Always group by day
  const sortedData = filteredData.map((point) => {
    const day = new Date(point.dayBucket);
    return {
      ...point,
      day: day.getTime(),
      count: Math.round(
        point.totalDurationSeconds / 60 / point.completedSessionCount,
      ),
      movingAvg: null,
      trend: null,
    };
  });

  return sortedData;
}
